import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, tap } from 'rxjs';
import { HttpService } from '../../../services/http.service';
import { requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';
import { GalleryComponent } from '../gallery-view/gallery-view.component';

interface APIResponse {
  id: any;
  name: any;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: APIResponse | APIResponse[];
}

@Component({
  selector: 'add-event-type',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputComponent, FormsModule],
  templateUrl: './add-event-type.component.html',
  styleUrls: ['./add-event-type.component.scss'],
})
export class AddEventTypeComponent implements OnInit, OnDestroy {
  @Input() initialData: any = null;
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() formValidation = new EventEmitter<boolean>();

  form!: FormGroup;
  pointsMethod: any = [];
  noteText: string = '';
  isLoading = true;
  selectedImage: any = null;
  selectedImageId: string = '';
  isImageTouched: boolean = false;
  private formSubmittedAttempted: boolean = false;
  private galleryDialogRef: any;
  mediaItems: any = [];
  private dialogConfirmListener?: (event: Event) => void;
  private readonly NON_EDITABLE_EVENT_TYPES = [
    'Teacher Work Experience',
    'Student External Event',
  ];
  customErrorMessages = {
    eventTypeName: { required: 'Please enter an event type name.' },
    eventTypeImageDescription: {
      required: 'Please enter an image description.',
    },
    pointsMethod: { required: 'Please select a points method.' },
    pointsAwarded: {
      required: 'Please enter a number.',
      pattern: 'Please enter a number.',
    },
  };

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupDialogListener();
    this.loadMediaItems();
    this.fetchPointsMethodDropDown();
  }

  ngOnDestroy(): void {
    this.cleanupDialogListener();
  }

  private initForm(): void {
    const eventTypeName = this.initialData
      ? (this.initialData['event type'] ?? this.initialData.eventType ?? '')
      : '';

    const pointsAwarded = this.initialData
      ? (this.initialData['PD points'] ?? this.initialData.pdPoints ?? '')
      : '';

    this.selectedImage = this.initialData?.eventTypeImage;
    this.selectedImageId = this.initialData?.imageId ?? '';

    const eventTypeImageDescription =
      this.initialData?.eventTypeImageDescription;

    this.form = this.fb.group({
      eventTypeName: [eventTypeName, requiredTrimmed],
      pointsMethod: [null, requiredTrimmed],
      eventTypeImageDescription: [eventTypeImageDescription],
      pointsAwarded: [
        pointsAwarded,
        [requiredTrimmed, Validators.pattern('^[0-9]*$')],
      ],
    });

    this.checkAndDisableEventTypeName(eventTypeName);

    this.formValidation.emit(this.form.valid && !!this.selectedImage);

    this.form.valueChanges.subscribe(() => {
      this.formValidation.emit(this.form.valid && !!this.selectedImage);
    });
  }

  private checkAndDisableEventTypeName(value: string): void {
    const control = this.form.get('eventTypeName');
    if (this.NON_EDITABLE_EVENT_TYPES.includes(value?.trim())) {
      control?.disable();
    } else {
      control?.enable();
    }
  }

  isEventTypeNameDisabled(): boolean {
    return this.form.get('eventTypeName')?.disabled ?? false;
  }

  private setInitialPointsMethod(): void {
    if (!this.initialData) return;

    const initialLabel =
      this.initialData['points method'] ?? this.initialData.pointsMethod;
    if (!initialLabel) return;

    const selectedPointsMethod = this.pointsMethod.find(
      (method: any) => method.label === initialLabel,
    );

    if (selectedPointsMethod) {
      this.form.patchValue({ pointsMethod: selectedPointsMethod.value });
      this.cdr.detectChanges();
    }
  }

  onNoteChange() {
    // console.log('Note entered:', this.noteText);
  }

  autoResize(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  isImageInvalid(): boolean {
    return !this.selectedImage;
  }

  markImageAsTouched(): void {
    this.isImageTouched = true;
  }

  private setupDialogListener(): void {
    this.dialogConfirmListener = () => this.handleDialogConfirm();
    document.addEventListener('dialogConfirm', this.dialogConfirmListener);
  }

  private cleanupDialogListener(): void {
    if (this.dialogConfirmListener) {
      document.removeEventListener('dialogConfirm', this.dialogConfirmListener);
    }
  }

  handleDialogConfirm(): void {
    this.markFormAsTouched();
    if (this.form.valid) {
      const formData = {
        ...this.form.getRawValue(),
        selectedImage: this.selectedImage,
        imageId: this.selectedImageId,
      };
      console.log('selectedImage:', this.selectedImage);
      this.formSubmitted.emit(formData);
    } else {
      this.formSubmittedAttempted = true;
    }
  }

  onSubmit(): void {
    this.handleDialogConfirm();
  }

  openGallery(): void {
    if (!this.mediaItems || this.mediaItems.length === 0) {
      this.showErrorToast(
        'Please upload at least one image in the Media Assets Library to be selected.',
      );
      return;
    }
    this.galleryDialogRef = this.dialog.open(GalleryComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: {
        // eventTypeOption: this.eventTypeOption,
      },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    this.galleryDialogRef.afterClosed().subscribe((selectedImage: any) => {
      if (selectedImage) {
        this.selectedImage = selectedImage;
        this.selectedImageId = selectedImage?.id ?? '';
        // this.isImageTouched = false;
        // this.formValidation.emit(this.form.valid && !!this.selectedImage);
        this.cdr.detectChanges();
      }
    });
  }

  removeSelectedImage(): void {
    this.selectedImage = null;
    this.selectedImageId = '';
    // this.formValidation.emit(this.form.valid && !!this.selectedImage);
    if (this.formSubmittedAttempted) {
      // this.isImageTouched = true;
    }
  }

  private markFormAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
  }

  isFormValid(): boolean {
    return this.form.valid && !!this.selectedImage;
  }

  loadMediaItems(): void {
    this.isLoading = true;

    this.httpService
      .get('eventtypes/getassetsimage')
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: any) => {
          if (response?.status && response?.data) {
            this.mediaItems = response.data;
          }
        },
        error: (err: any) => {
          console.error('Failed to load media:', err);
          this.showErrorToast(err?.message || 'Failed to load media');
        },
      });
  }
  private fetchPointsMethodDropDown() {
    this.isLoading = true;
    this.httpService
      .get<ApiDataResponse>(`pointsmethod/dropdown`)
      .pipe(
        tap({
          next: (response) => {
            this.pointsMethod = Array.isArray(response.data)
              ? response.data.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))
              : [];
            this.setInitialPointsMethod();
          },
          error: () => {
            this.pointsMethod = [];
            this.showErrorToast(
              'Something went wrong. Please try again later.',
            );
          },
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
